import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  ClickAwayListener,
  Fade,
  FormControlLabel,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Column } from '@tanstack/react-table';
import { useMemo, useState, type SyntheticEvent } from 'react';

import { GroupTitle } from '../option-menu/groupTitle';
import { getProperties } from '../queryBuilder/propertiesUtils';
import { PropertyGroups } from '../queryBuilder/propertyGroups';
import { Icon } from '../shared/icon';

interface ColumnSelectorProps<T> {
  columns: Column<T>[];
}

export const ColumnSelector = <T,>({ columns }: ColumnSelectorProps<T>) => {
  'use no memo';

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [open, setOpen] = useState(false);

  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorEl?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const groupedColumns = useMemo(
    () =>
      columns.reduce<Record<string, Column<T>[]>>((acc, column) => {
        if (column.getCanHide()) {
          getProperties(column).forEach((prop) => {
            acc[prop.group] = acc[prop.group] || [];
            if (!acc[prop.group].includes(column)) {
              acc[prop.group].push(column);
            }
          });
        }
        return acc;
      }, {}),
    [columns]
  );

  const categories = Object.keys(groupedColumns);

  return (
    <>
      <Tooltip title='Manage columns'>
        <Icon
          data-testid='column-selector'
          ref={setAnchorEl}
          iconClass='material-symbols-splitscreen-vertical-add-outline-rounded'
          isActive={open}
          onClick={() => setOpen(!open)}
        />
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={'left-end'}
        transition
        disablePortal
        sx={{ zIndex: 10 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              sx={{
                borderRadius: '16px',
                boxShadow: 'var(--mui-customShadows-lg)',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Card
                  sx={{
                    boxShadow: 'var(--mui-customShadows-panelMd)',
                    width: '308px',
                  }}
                >
                  <CardHeader
                    className='py-[12px] px-[24px]'
                    action={
                      <Tooltip title={'Close'}>
                        <Icon
                          iconClass='material-symbols-close-rounded'
                          onClick={handleClose}
                        />
                      </Tooltip>
                    }
                    sx={{
                      padding: '12px 24px',
                    }}
                    title={<Typography variant='h3'>Manage Columns</Typography>}
                  />
                  <CardContent
                    sx={{
                      height: 500,
                      overflow: 'auto',
                      padding: '0',
                    }}
                  >
                    {[''].concat(Object.keys(PropertyGroups)).map((group) => {
                      const columns = groupedColumns[group];
                      if (!columns) {
                        return null;
                      }
                      const title = PropertyGroups[group]?.title;
                      return (
                        <Box key={group}>
                          {categories.length > 1 && title && (
                            <GroupTitle title={title} />
                          )}
                          {columns.map((column) => {
                            if (!column.getCanHide()) {
                              return null;
                            }

                            return (
                              <div key={column.id}>
                                <FormControlLabel
                                  className='w-full'
                                  control={
                                    <Checkbox
                                      sx={{ padding: '10px 20px' }}
                                      size='small'
                                      checked={column.getIsVisible()}
                                      onChange={() => {
                                        column.toggleVisibility();
                                      }}
                                    />
                                  }
                                  label={`${column.columnDef.header}`}
                                />
                              </div>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
